import { useEffect, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  Check,
  Compass,
  LockKeyhole,
  Mail,
  Menu,
  Plane,
  Radio,
  ScanLine,
  Sparkles,
  X as XIcon,
} from "lucide-react";
import { Toaster, toast } from "@/components/ui/sonner";
import "@/App.css";

const LOGO_URL =
  "https://customer-assets-v7afamib.emergentagent.net/job_18b76273-4f07-429c-a515-15b33e45c4b6/artifacts/aaa5u6h2_448687E2-3064-4A3C-A647-0E756A41B478.png";
const AVIATION_IMAGE_URL =
  "https://static.prod-images.emergentagent.com/jobs/18b76273-4f07-429c-a515-15b33e45c4b6/images/5732122f69ee1dbf4eaebec0aeb8509182bca497310f2abeb6751e3e568cadc5.jpeg";
const REVEAL_HERO_IMAGE_URL =
  "https://static.prod-images.emergentagent.com/jobs/18b76273-4f07-429c-a515-15b33e45c4b6/images/74cce7caf4abe1021b375780d14455423fc725cc3bba687fe5b2fe25c508e4bb.jpeg";

const navItems = [
  { href: "#manifesto", label: "The signal", testId: "nav-link-manifesto" },
  { href: "#access", label: "Access", testId: "nav-link-access" },
];

const manifestoItems = [
  {
    index: "01",
    label: "FIND THE SIGNAL",
    title: "Look up, differently.",
    text: "Turn a passing silhouette into a moment worth keeping.",
  },
  {
    index: "02",
    label: "MAKE THE SIGHTING",
    title: "Spot with intent.",
    text: "A sharper field guide for the aircraft already above you.",
  },
  {
    index: "03",
    label: "BUILD THE LOG",
    title: "Collect the sky.",
    text: "Your sightings become a personal record of everywhere overhead.",
  },
  {
    index: "04",
    label: "JOIN THE FREQUENCY",
    title: "Never spot alone.",
    text: "A new way for aviation people to compare notes and keep looking up.",
  },
];

const marqueeItems = [
  "LIVE FLIGHT LOG",
  "SKYSEEKER / 01",
  "ALT 45.000 FT",
  "SPOT · CAPTURE · SHARE",
];

const socialLinks = [
  { name: "Instagram", href: "https://www.instagram.com/skyseekerapp?igsi=YmdmanVoZjY2cm50&utm_source=qr", testId: "footer-social-instagram" },
  { name: "TikTok", href: "https://www.tiktok.com/@skyseekerapp?_r=1&_t=ZN-99RvzVj1xIc", testId: "footer-social-tiktok" },
  { name: "X", href: "https://x.com/skyseekerapp?s=11&t=tYEbR8kR-CS4TKfq5ymBTg", testId: "footer-social-x" },
  { name: "YouTube", href: "https://youtube.com/@skyseeker", testId: "footer-social-youtube" },
];

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function storageValue(storage, key, makeValue) {
  try {
    const current = storage.getItem(key);
    if (current) return current;
    const created = makeValue();
    storage.setItem(key, created);
    return created;
  } catch {
    return makeValue();
  }
}

function analyticsContext() {
  const visitorId = storageValue(window.localStorage, "sky-seeker-visitor-id", () => crypto.randomUUID());
  const sessionId = storageValue(window.sessionStorage, "sky-seeker-session-id", () => crypto.randomUUID());
  let stored = {};
  try {
    stored = JSON.parse(window.sessionStorage.getItem("sky-seeker-attribution") || "{}");
  } catch {
    stored = {};
  }
  const params = new URLSearchParams(window.location.search);
  const attribution = Object.fromEntries(UTM_KEYS.map((key) => [key, params.get(key) || stored[key] || undefined]));
  if (Object.values(attribution).some(Boolean)) {
    try { window.sessionStorage.setItem("sky-seeker-attribution", JSON.stringify(attribution)); } catch { }
  }
  return { visitor_id: visitorId, session_id: sessionId, referrer: document.referrer || undefined, ...attribution };
}

function sendAnalyticsEvent(eventType, details = {}) {
  const context = analyticsContext();
  if (eventType === "signup_start") {
    try {
      if (window.sessionStorage.getItem("sky-seeker-signup-started")) return;
      window.sessionStorage.setItem("sky-seeker-signup-started", "1");
    } catch { }
  }
  return fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/collect`, {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event_type: eventType, path: window.location.pathname, ...context, ...details }),
  }).catch(() => undefined);
}

function usePublicAnalytics(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const thresholds = new Set();
    let maxScroll = 0;
    const sendScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const depth = scrollable > 0 ? Math.min(100, Math.round((window.scrollY / scrollable) * 100)) : 100;
      maxScroll = Math.max(maxScroll, depth);
      [25, 50, 75, 100].forEach((threshold) => {
        if (depth >= threshold && !thresholds.has(threshold)) {
          thresholds.add(threshold);
          sendAnalyticsEvent("scroll_depth", { scroll_depth: threshold });
        }
      });
    };
    const startedAt = Date.now();
    sendAnalyticsEvent("page_view");
    const engagementTimer = window.setTimeout(() => sendAnalyticsEvent("engagement"), 15000);
    const endSession = () => sendAnalyticsEvent("session_end", { duration_ms: Date.now() - startedAt, scroll_depth: maxScroll });
    window.addEventListener("scroll", sendScroll, { passive: true });
    window.addEventListener("pagehide", endSession);
    return () => {
      window.clearTimeout(engagementTimer);
      window.removeEventListener("scroll", sendScroll);
      window.removeEventListener("pagehide", endSession);
    };
  }, [enabled]);
}

function SocialGlyph({ name }) {
  if (name === "Instagram") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.7" r="1" className="social-dot" />
      </svg>
    );
  }

  if (name === "TikTok") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M14.3 3.5v10.1a3.7 3.7 0 1 1-3-3.6v2.3a1.4 1.4 0 1 0 .7 1.3V3.5h2.3c.2 1.3 1 2.4 2.2 3v2.4a6.2 6.2 0 0 1-2.2-1V3.5Z" />
      </svg>
    );
  }

  if (name === "YouTube") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 7.4a2.8 2.8 0 0 0-2-2C17.3 5 12 5 12 5s-5.3 0-7 .4a2.8 2.8 0 0 0-2 2A29 29 0 0 0 2.6 12 29 29 0 0 0 3 16.6a2.8 2.8 0 0 0 2 2c1.7.4 7 .4 7 .4s5.3 0 7-.4a2.8 2.8 0 0 0 2-2 29 29 0 0 0 .4-4.6 29 29 0 0 0-.4-4.6ZM10 15.5v-7l6 3.5-6 3.5Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 4h3.7l3.2 4.3L15.7 4H19l-5.5 6.2L19.5 20h-3.7l-3.8-5-4.2 5H4.5l5.8-6.8L5 4Zm2.8 1.8 8.9 12.4h1.1L8.9 5.8H7.8Z" />
    </svg>
  );
}

function Header({ scrolled }) {
  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="site-header__inner">
        <a className="brand-lockup" href="#top" data-testid="header-brand-logo" aria-label="Sky Seeker home">
          <img src={LOGO_URL} alt="Sky Seeker logo" className="brand-logo" />
        </a>
        <a href="#waitlist" className="header-cta" data-testid="header-waitlist-button">
          Join the waitlist <ArrowUpRight size={15} strokeWidth={1.7} />
        </a>
      </div>
    </header>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || submitted) return;
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Enter a valid email address to continue.");
      void fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mailchimp/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), ...analyticsContext() }),
      }).catch(() => {});
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mailchimp/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), ...analyticsContext() }),
      });
      const body = await response.json().catch(() => ({}));
      if (response.ok) {
        setSubmitted(true);
        toast.success(body.message || "You’re subscribed to Sky Seeker updates.");
      } else if (response.status === 409) {
        setError(body.detail?.message || "This email is already subscribed.");
      } else if (response.status === 422) {
        setError("Enter a valid email address to continue.");
      } else {
        setError(body.detail?.message || "We could not complete your signup. Please try again.");
      }
    } catch {
      setError("Unable to reach the signup service. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reveal-form-wrap" id="waitlist" data-testid="waitlist-form-container">
      {submitted ? (
        <div className="reveal-success" data-testid="waitlist-success-message" role="status">
          <div className="success-icon"><Check size={18} strokeWidth={2.5} /></div>
          <div><strong>You’re on the list.</strong><p>We’ll be in touch before the big reveal.</p></div>
        </div>
      ) : (
        <form className="reveal-form" onSubmit={handleSubmit} noValidate>
          <label className="sr-only" htmlFor="waitlist-email">Email address</label>
          <div className={`reveal-email-field ${error ? "has-error" : ""}`}>
            <Mail size={19} strokeWidth={1.5} aria-hidden="true" />
            <input id="waitlist-email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); setError(""); }} onFocus={() => sendAnalyticsEvent("signup_start")} placeholder="Enter your email address" autoComplete="email" data-testid="waitlist-email-input" aria-invalid={Boolean(error)} aria-describedby={error ? "waitlist-email-error" : "waitlist-consent-note"} disabled={submitting} />
          </div>
          <button type="submit" className="reveal-submit" disabled={submitting} data-testid="waitlist-submit-button">{submitting ? "Joining..." : "Join the journey"}{!submitting && <ArrowUpRight size={18} strokeWidth={1.8} />}</button>
          {error && <p className="reveal-form-error" id="waitlist-email-error" data-testid="waitlist-error-message">{error}</p>}
          <p className="reveal-consent" id="waitlist-consent-note" data-testid="waitlist-consent-note">By joining, you agree to receive Sky Seeker launch updates. Unsubscribe anytime.</p>
        </form>
      )}
    </div>
  );
}

function HeroVisual() {
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  const handlePointerMove = (event) => {
    if (event.pointerType === "touch") return;
    const rect = event.currentTarget.getBoundingClientRect();
    setPointer({
      x: event.clientX - (rect.left + rect.width / 2),
      y: event.clientY - (rect.top + rect.height / 2),
    });
  };

  return (
    <div
      className="hero-visual"
      style={{ "--pointer-x": `${pointer.x}px`, "--pointer-y": `${pointer.y}px` }}
      onPointerMove={handlePointerMove}
      onPointerLeave={() => setPointer({ x: 0, y: 0 })}
    >
      <div className="hero-visual__halo" aria-hidden="true" />
      <div className="hero-visual__orbit" aria-hidden="true">
        <svg className="orbit-svg" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r="49" />
          <circle cx="17" cy="18" r="1.4" />
        </svg>
      </div>
      <figure className="hero-photo-frame" data-testid="hero-spotlight-frame">
        <div className="hero-image-layer">
          <img
            src={AVIATION_IMAGE_URL}
            alt="Private jet resting on a golden runway at dusk"
            className="hero-aviation-image"
            data-testid="hero-aviation-image"
          />
          <div className="hero-photo-shade" aria-hidden="true" />
        </div>
        <figcaption className="hero-photo-caption">
          <span>OBSERVATION / 001</span>
          <strong>THE SKY IS YOURS TO FIND</strong>
        </figcaption>
      </figure>
      <div className="hero-readout">
        <span><Radio size={14} /> LIVE / ABOVE YOU</span>
        <span>51° 28′ N&nbsp;&nbsp; 0° 27′ W</span>
      </div>
    </div>
  );
}

function Manifesto() {
  return (
    <section className="manifesto-section page-shell reveal" id="manifesto" data-reveal data-testid="manifesto-section">
      <div className="manifesto-heading">
        <div>
          <p className="eyebrow"><ScanLine size={13} /> Flight manifesto</p>
          <h2>A better reason<br /><em>to look up.</em></h2>
        </div>
        <p className="manifesto-intro">The first release is intentionally quiet. A signal for people who already know the feeling.</p>
      </div>
      <div className="manifesto-grid">
        {manifestoItems.map((item, index) => (
          <article className={`manifesto-card manifesto-card--${index + 1}`} key={item.index} data-testid={`manifesto-card-${item.index}`} style={{ "--reveal-delay": `${index * 90}ms` }}>
            <div className="manifesto-card__top"><span>[{item.index}]</span><ArrowUpRight size={17} strokeWidth={1.5} /></div>
            <p className="manifesto-card__label">{item.label}</p>
            <h3>{item.title}</h3>
            <p className="manifesto-card__text">{item.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function SocialFooter() {
  return (
    <footer className="site-footer page-shell" data-testid="footer-container">
      <div className="social-footer-band">
        <div className="social-footer-heading">
          <p className="eyebrow"><Sparkles size={13} /> Follow Sky Seeker</p>
          <h2 data-testid="footer-motto">Track, Collect, Explore</h2>
          <p>Stay close to the launch.</p>
        </div>
        <div className="social-links social-links--prominent">
          {socialLinks.map((social) => (
            <a href={social.href} target="_blank" rel="noreferrer" aria-label={`Sky Seeker on ${social.name}`} data-testid={social.testId} key={social.name}>
              <SocialGlyph name={social.name} />
              <span>{social.name}</span>
            </a>
          ))}
        </div>
      </div>
      <div className="footer-bottom">
        <span data-testid="footer-legal-text">© 2026 Sky Seeker.</span>
        <span>Track, Collect, Explore</span>
      </div>
    </footer>
  );
}

function SimpleHeroVisual() {
  return (
    <figure className="hero-photo-frame" data-testid="hero-spotlight-frame">
      <img
        src={AVIATION_IMAGE_URL}
        alt="Passenger aircraft flying through a deep blue evening sky"
        className="hero-aviation-image"
        data-testid="hero-aviation-image"
      />
      <div className="hero-photo-shade" aria-hidden="true" />
      <figcaption className="hero-photo-caption">
        <span>SKY SEEKER / IN FLIGHT</span>
      </figcaption>
    </figure>
  );
}


function dashboardErrorMessage(body, fallback) {
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function formatMetric(value) {
  return new Intl.NumberFormat("en-GB").format(value || 0);
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function MetricChart({ data, metric, color }) {
  if (!data?.length) return <div className="analytics-empty-chart" data-testid="analytics-chart-empty">No activity recorded for this period.</div>;
  const values = data.map((item) => Number(item[metric] || 0));
  const max = Math.max(...values, 1);
  const points = values.map((value, index) => `${(index / Math.max(values.length - 1, 1)) * 100},${92 - (value / max) * 72}`).join(" ");
  return (
    <div className="metric-chart" data-testid={`analytics-chart-${metric}`}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-label={`${metric} over time`} role="img">
        <path d="M 0 92 H 100 M 0 56 H 100 M 0 20 H 100" className="chart-grid-line" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
        {values.map((value, index) => <circle key={`${index}-${value}`} cx={(index / Math.max(values.length - 1, 1)) * 100} cy={92 - (value / max) * 72} r="1.25" fill={color} />)}
      </svg>
      <div className="chart-axis"><span>{data[0].date}</span><span>{data[data.length - 1].date}</span></div>
    </div>
  );
}

function Dashboard() {
  const [status, setStatus] = useState("checking");
  const [password, setPassword] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [rangeKey, setRangeKey] = useState("7d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [chartMetric, setChartMetric] = useState("visits");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState("");

  const loadDashboard = async (key = rangeKey, start = customStart, end = customEnd) => {
    const query = new URLSearchParams({ range: key });
    if (key === "custom" && start) query.set("start", start);
    if (key === "custom" && end) query.set("end", end);
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/dashboard?${query.toString()}`, { credentials: "include" });
    const body = await response.json().catch(() => ({}));
    if (response.status === 401) {
      setStatus("signed-out");
      throw new Error("Dashboard sign-in required.");
    }
    if (!response.ok) throw new Error(dashboardErrorMessage(body, "Unable to load analytics."));
    setDashboard(body);
  };

  useEffect(() => {
    let active = true;
    const checkSession = async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/session`, { credentials: "include" });
      if (!active) return;
      if (!response.ok) {
        setStatus("signed-out");
        return;
      }
      try {
        await loadDashboard("7d");
        if (active) setStatus("signed-in");
      } catch (sessionError) {
        if (active) {
          setError(sessionError.message);
          setStatus("signed-out");
        }
      }
    };
    checkSession();
    return () => { active = false; };
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(dashboardErrorMessage(body, "Unable to unlock the dashboard."));
      await loadDashboard("7d");
      setStatus("signed-in");
      setPassword("");
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRangeChange = async (event) => {
    const next = event.target.value;
    setRangeKey(next);
    if (next !== "custom") {
      setError("");
      try { await loadDashboard(next); } catch (rangeError) { setError(rangeError.message); }
    }
  };

  const handleCustomRange = async (event) => {
    event.preventDefault();
    setError("");
    try { await loadDashboard("custom", customStart, customEnd); } catch (rangeError) { setError(rangeError.message); }
  };

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const query = new URLSearchParams({ type, range: rangeKey });
      if (rangeKey === "custom" && customStart) query.set("start", customStart);
      if (rangeKey === "custom" && customEnd) query.set("end", customEnd);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/export?${query.toString()}`, { credentials: "include" });
      if (!response.ok) throw new Error("Export could not be created.");
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `sky-seeker-${type}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExporting("");
    }
  };

  const handleLogout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/logout`, { method: "POST", credentials: "include" });
    setDashboard(null);
    setStatus("signed-out");
  };

  if (status === "checking") return <main className="dashboard-page" data-testid="dashboard-loading">Checking owner access...</main>;

  if (status === "signed-out") {
    return (
      <main className="dashboard-page" data-testid="dashboard-login-page">
        <div className="dashboard-shell dashboard-shell--login">
          <a href="/" className="dashboard-brand" data-testid="dashboard-brand-link"><img src={LOGO_URL} alt="Sky Seeker logo" /></a>
          <p className="eyebrow">Private owner access</p>
          <h1>Landing page<br /><em>analytics.</em></h1>
          <p className="dashboard-intro">Measure the Sky Seeker pre-launch without exposing visitor information.</p>
          <form className="dashboard-login-form" onSubmit={handleLogin} noValidate>
            <label htmlFor="dashboard-password">Owner password</label>
            <input id="dashboard-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" data-testid="dashboard-password-input" />
            <button type="submit" disabled={busy} data-testid="dashboard-login-button">{busy ? "Unlocking..." : "Unlock dashboard"} <ArrowUpRight size={17} /></button>
            {error && <p className="dashboard-error" data-testid="dashboard-login-error" role="alert">{error}</p>}
          </form>
          <a href="/" className="dashboard-back-link" data-testid="dashboard-back-link">Back to Sky Seeker</a>
        </div>
      </main>
    );
  }

  if (!dashboard) return <main className="dashboard-page" data-testid="dashboard-loading">Loading analytics...</main>;
  const { kpis, comparisons, range, traffic_over_time: traffic, signups_over_time: signups, funnel, traffic_sources: sources, campaigns, devices, geographies, recent_activity: activity, survey } = dashboard;
  const kpiCards = [
    ["unique-visitors", "Unique visitors", kpis.unique_visitors, comparisons.unique_visitors],
    ["email-signups", "Email sign-ups", kpis.email_signups, comparisons.email_signups],
    ["email-conversion", "Email conversion rate", formatPercent(kpis.email_conversion_rate), null],
    ["survey-completions", "Survey completions", kpis.survey_completions, comparisons.survey_completions],
    ["survey-completion-rate", "Survey completion rate", formatPercent(kpis.survey_completion_rate), null],
    ["total-visits", "Total visits", kpis.total_visits, comparisons.total_visits],
  ];

  return (
    <main className="dashboard-page" data-testid="dashboard-page">
      <div className="dashboard-shell dashboard-shell--wide">
        <header className="dashboard-header">
          <a href="/" className="dashboard-brand" data-testid="dashboard-brand-link"><img src={LOGO_URL} alt="Sky Seeker logo" /></a>
          <div className="dashboard-header-actions">
            <span className="dashboard-live-status"><span /> Live data</span>
            <button type="button" className="dashboard-logout" onClick={handleLogout} data-testid="dashboard-logout-button">Lock dashboard</button>
          </div>
        </header>
        <div className="dashboard-title-row">
          <div>
            <p className="eyebrow">Sky Seeker / private owner view</p>
            <h1>Landing Page<br /><em>Analytics.</em></h1>
          </div>
          <div className="dashboard-controls">
            <label htmlFor="analytics-range">Date range</label>
            <select id="analytics-range" value={rangeKey} onChange={handleRangeChange} data-testid="analytics-range-select">
              <option value="today">Today</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Date Range</option>
            </select>
            {rangeKey === "custom" && <form className="custom-range-form" onSubmit={handleCustomRange}><input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} data-testid="analytics-custom-start" /><input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} data-testid="analytics-custom-end" /><button type="submit" data-testid="analytics-custom-apply">Apply</button></form>}
            <span className="dashboard-range-label" data-testid="analytics-range-label">{range.label}</span>
          </div>
        </div>
        {error && <p className="dashboard-error dashboard-inline-error" data-testid="dashboard-data-error" role="alert">{error}</p>}
        <section className="analytics-kpi-grid" data-testid="analytics-kpi-grid">
          {kpiCards.map(([id, label, value, comparison]) => (
            <article className={`analytics-kpi analytics-kpi--${id}`} key={id} data-testid={`analytics-kpi-${id}`}>
              <p>{label}</p><strong>{typeof value === "number" ? formatMetric(value) : value}</strong>
              <span>{comparison === null || comparison === undefined ? "Current period" : comparison === 0 ? "0% vs previous period" : `${comparison > 0 ? "+" : ""}${comparison}% vs previous period`}</span>
            </article>
          ))}
        </section>
        <section className="analytics-secondary-grid" data-testid="analytics-secondary-metrics">
          <div><span>New visitors</span><strong>{formatMetric(kpis.new_visitors)}</strong></div><div><span>Returning visitors</span><strong>{formatMetric(kpis.returning_visitors)}</strong></div><div><span>Sessions</span><strong>{formatMetric(kpis.sessions)}</strong></div><div><span>Avg session</span><strong>{kpis.average_session_duration_seconds}s</strong></div><div><span>Engagement rate</span><strong>{formatPercent(kpis.engagement_rate)}</strong></div><div><span>Scroll depth</span><strong>{formatPercent(kpis.average_scroll_depth)}</strong></div>
        </section>
        <section className="analytics-panel analytics-chart-panel" data-testid="visitors-over-time-panel">
          <div className="analytics-panel-heading"><div><p className="eyebrow">01 / traffic</p><h2>Visitors over time</h2></div><select value={chartMetric} onChange={(event) => setChartMetric(event.target.value)} data-testid="visitors-chart-metric"><option value="visits">Visits</option><option value="unique_visitors">Unique visitors</option><option value="sessions">Sessions</option></select></div>
          <MetricChart data={traffic} metric={chartMetric} color="#d4af37" />
        </section>
        <section className="analytics-two-column">
          <div className="analytics-panel" data-testid="email-signups-over-time-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">02 / conversion</p><h2>Email sign-ups over time</h2></div></div><MetricChart data={signups} metric="signups" color="#8fb5ff" /></div>
          <div className="analytics-panel" data-testid="conversion-funnel-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">03 / north star</p><h2>Conversion funnel</h2></div></div><div className="analytics-funnel">{funnel.map((stage) => <div className="funnel-row" key={stage.stage}><div><span>{stage.stage}</span><strong>{formatMetric(stage.value)}</strong></div><b>{formatPercent(stage.conversion_rate)}</b></div>)}</div></div>
        </section>
        <section className="analytics-panel" data-testid="traffic-sources-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">04 / attribution</p><h2>Traffic sources</h2></div><button className="dashboard-export" onClick={() => handleExport("visitors")} disabled={Boolean(exporting)} data-testid="analytics-export-visitors">{exporting === "visitors" ? "Preparing..." : "Export CSV"}</button></div><AnalyticsTable headers={["Source", "Visitors", "Sign-ups", "Conversion"]} rows={sources.map((row) => [row.source, formatMetric(row.visitors), formatMetric(row.signups), formatPercent(row.conversion_rate)])} empty="No attributed traffic recorded yet." testId="traffic-sources-table" /></section>
        <section className="analytics-panel" data-testid="campaign-performance-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">05 / campaigns</p><h2>Campaign performance</h2></div><button className="dashboard-export" onClick={() => handleExport("campaigns")} disabled={Boolean(exporting)} data-testid="analytics-export-campaigns">{exporting === "campaigns" ? "Preparing..." : "Export CSV"}</button></div><AnalyticsTable headers={["Campaign", "Visitors", "Sign-ups", "Conversion", "Survey completions"]} rows={campaigns.map((row) => [row.campaign, formatMetric(row.visitors), formatMetric(row.signups), formatPercent(row.conversion_rate), formatMetric(row.survey_completions)])} empty="No UTM campaigns recorded yet." testId="campaign-performance-table" /></section>
        <section className="analytics-two-column">
          <div className="analytics-panel" data-testid="device-breakdown-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">06 / device</p><h2>Device breakdown</h2></div></div><BreakdownList rows={devices.map((row) => ({ label: row.device, value: row.visitors, percentage: row.percentage, detail: `${formatPercent(row.conversion_rate)} conversion` }))} empty="No device data recorded yet." /></div>
          <div className="analytics-panel" data-testid="geographic-breakdown-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">07 / geography</p><h2>Country & region</h2></div></div><AnalyticsTable headers={["Country", "Region", "Visitors", "%"]} rows={geographies.map((row) => [row.country, row.region, formatMetric(row.visitors), formatPercent(row.percentage)])} empty="No aggregate geography recorded yet." testId="geographic-breakdown-table" /></div>
        </section>
        <section className="analytics-two-column">
          <div className="analytics-panel" data-testid="survey-analytics-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">08 / survey</p><h2>Survey analytics</h2></div><button className="dashboard-export" onClick={() => handleExport("survey")} disabled={Boolean(exporting)} data-testid="analytics-export-survey">{exporting === "survey" ? "Preparing..." : "Export CSV"}</button></div><div className="survey-zero-state"><strong>Survey tracking is ready</strong><span>{formatMetric(survey.starts)} starts · {formatMetric(survey.completions)} completions · {formatPercent(survey.completion_rate)} completion</span><small>Survey responses will appear here when a survey is added.</small></div></div>
          <div className="analytics-panel" data-testid="recent-activity-panel"><div className="analytics-panel-heading"><div><p className="eyebrow">09 / activity</p><h2>Recent activity</h2></div></div><ActivityList rows={activity} /></div>
        </section>
        <p className="dashboard-footnote"><LockKeyhole size={13} /> Privacy-conscious totals only. Conversion and attribution use sign-ups recorded after analytics tracking began.</p>
      </div>
    </main>
  );
}

function AnalyticsTable({ headers, rows, empty, testId }) {
  return rows.length ? <div className="analytics-table-wrap" data-testid={testId}><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={`${row[0]}-${index}`}>{row.map((cell, cellIndex) => <td key={`${cell}-${cellIndex}`}>{cell}</td>)}</tr>)}</tbody></table></div> : <div className="analytics-empty-state" data-testid={`${testId}-empty`}>{empty}</div>;
}

function BreakdownList({ rows, empty }) {
  if (!rows.length) return <div className="analytics-empty-state">{empty}</div>;
  return <div className="breakdown-list">{rows.map((row) => <div className="breakdown-row" key={row.label}><div className="breakdown-label"><span>{row.label}</span><b>{formatMetric(row.value)}</b></div><div className="breakdown-bar"><span style={{ width: `${Math.min(row.percentage, 100)}%` }} /></div><small>{formatPercent(row.percentage)} · {row.detail}</small></div>)}</div>;
}

function ActivityList({ rows }) {
  if (!rows.length) return <div className="analytics-empty-state">No activity recorded yet.</div>;
  return <div className="activity-list">{rows.map((row, index) => <div className="activity-row" key={`${row.created_at}-${index}`}><span className="activity-dot" /><div><strong>{row.event}</strong><small>{row.source} · {row.device}</small></div><time>{new Date(row.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</time></div>)}</div>;
}

const featurePillars = [
  { label: "TRACK", icon: "track" },
  { label: "COLLECT", icon: "collect" },
  { label: "PROGRESS", icon: "progress" },
  { label: "COMMUNITY", icon: "community" },
  { label: "EXPLORE", icon: "explore" },
];

function FeatureIcon({ name }) {
  if (name === "track") return <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="17" /><circle cx="24" cy="24" r="10" /><path d="M24 7v34M7 24h34M12 36l24-24" /><path d="m23 17 3 5 7 2-7 2-3 5-2-5-7-2 7-2 2-5Z" fill="currentColor" stroke="none" /></svg>;
  if (name === "collect") return <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="10" y="11" width="24" height="30" rx="2" transform="rotate(-8 10 11)" /><rect x="17" y="7" width="24" height="30" rx="2" transform="rotate(8 17 7)" /><path d="m24 17 3 5 6 2-6 2-3 5-2-5-6-2 6-2 2-5Z" fill="currentColor" stroke="none" /></svg>;
  if (name === "progress") return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M8 38h32M11 33l8-9 6 5 12-15" /><path d="M29 14h8v8" /><circle cx="35" cy="35" r="6" /><path d="m32.5 35 2 2 3.5-4" /></svg>;
  if (name === "community") return <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="15" r="6" /><circle cx="10" cy="21" r="4" /><circle cx="38" cy="21" r="4" /><path d="M13 38c1-7 5-11 11-11s10 4 11 11M3 36c.5-4.5 3-7 7-7 2 0 4 1 5 3M45 36c-.5-4.5-3-7-7-7-2 0-4 1-5 3" /></svg>;
  return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 42s13-12 13-23a13 13 0 1 0-26 0c0 11 13 23 13 23Z" /><circle cx="24" cy="19" r="4" /><path d="M7 39c8-1 14-5 18-11M7 39l7-1M7 39l2-6" strokeDasharray="2 3" /></svg>;
}

function CalendarMark() {
  return <svg viewBox="0 0 28 28" aria-hidden="true"><rect x="4" y="6" width="20" height="18" rx="2" /><path d="M8 4v5M20 4v5M4 11h20" /></svg>;
}

function PrivacyPolicy() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="reveal-privacy-link" onClick={() => setOpen(true)} data-testid="privacy-policy-link">Privacy Policy</button>
      {open && <div className="privacy-backdrop" role="presentation" onClick={() => setOpen(false)}><section className="privacy-dialog" role="dialog" aria-modal="true" aria-labelledby="privacy-title" onClick={(event) => event.stopPropagation()} data-testid="privacy-policy-dialog"><button type="button" className="privacy-close" onClick={() => setOpen(false)} aria-label="Close privacy policy" data-testid="privacy-policy-close">×</button><p className="eyebrow">Sky Seeker Ltd</p><h2 id="privacy-title">Privacy Policy</h2><p>We use your email only to send Sky Seeker launch updates. Anonymous page analytics help us understand campaign performance without exposing personal visitor details.</p><p>You can unsubscribe from launch updates at any time.</p></section></div>}
    </>
  );
}

function ComingSoonPage() {
  return (
    <div className="reveal-page" data-testid="coming-soon-page">
      <svg className="logo-filter-defs" aria-hidden="true"><defs><filter id="logo-alpha" colorInterpolationFilters="sRGB"><feColorMatrix type="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0.85 0.85 0.85 0 -0.5" /></filter></defs></svg>
      <header className="reveal-header" data-testid="reveal-header">
        <img src={LOGO_URL} alt="Sky Seeker logo" className="reveal-logo" data-testid="reveal-logo" />
      </header>
      <main>
        <section className="reveal-hero" id="top">
          <div className="reveal-hero-stage">
            <div className="reveal-hero-image"><img src={REVEAL_HERO_IMAGE_URL} alt="Airbus A340 on an airport runway at blue hour" data-testid="reveal-hero-image" /><span /></div>
          <div className="reveal-hero-content page-shell">
            <h1 className="reveal-main-headline" data-testid="reveal-headline"><span>ARE YOU READY TO</span><strong>ELEVATE</strong><span>YOUR AVIATION</span><span>EXPERIENCE?</span></h1>
            <div className="reveal-date-panel" data-testid="reveal-date-panel">
              <div className="reveal-calendar"><CalendarMark /></div>
              <strong>4TH DECEMBER</strong>
            </div>
            <div className="reveal-signup" data-testid="reveal-signup-section">
              <p>Be the first to discover Sky Seeker.<br />Join the journey before the big reveal.</p>
              <WaitlistForm />
            </div>
          </div>
          </div>
        </section>
        <section className="feature-pillars page-shell" data-testid="feature-pillars-section">
          <div className="feature-pillar-grid">
            {featurePillars.map(({ label, icon }) => <article className="feature-pillar" key={label} data-testid={`feature-pillar-${label.toLowerCase()}`}><FeatureIcon name={icon} /><strong>{label}</strong></article>)}
          </div>
        </section>
      </main>
      <footer className="reveal-footer page-shell" data-testid="reveal-footer">
        <div className="reveal-footer-line"><span /><span className="footer-mark-label">SKY SEEKER</span><span /></div>
        <div className="reveal-socials">
          {socialLinks.map((social) => <a href={social.href} target="_blank" rel="noreferrer" aria-label={`Sky Seeker on ${social.name}`} data-testid={`footer-social-${social.name.toLowerCase()}`} key={social.name}><SocialGlyph name={social.name} /></a>)}
        </div>
        <div className="reveal-footer-meta"><PrivacyPolicy /><span className="footer-divider">|</span><span data-testid="footer-copyright">© 2026 Sky Seeker Ltd. All Rights Reserved.</span></div>
      </footer>
      <Toaster position="bottom-right" closeButton />
    </div>
  );
}

function App() {
  const [dashboardPath] = useState(() => window.location.pathname === "/owner");
  usePublicAnalytics(!dashboardPath);
  if (dashboardPath) return <Dashboard />;
  return <ComingSoonPage />;
}

export default App;
